import { useCallback, useEffect, useRef } from 'react'

import { noop } from '@/utils/misc.ts'

declare global {
  interface Window {
    _wq: Array<{
      id: string
      onReady: (video: any) => void
    }>
  }
}

interface WistiaPlayerProps {
  videoId: string
  onVideoEnds?: () => void
}

/**
 * Wistia player implemented in embedded code way because:
 * 1. wistia doesn't have react-wistia library
 * 2. this is most common way of inserting <script/> into react jsx without extra libraries
 *
 * Todo: rewrite video player with video html tag
 */
export const VideoPlayer = ({
  videoId,
  onVideoEnds = noop,
}: WistiaPlayerProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  const onScriptsLoad = useCallback(() => {
    window._wq = window._wq || []
    window._wq.push({
      id: videoId,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onReady: function (video: any) {
        video.bind('end', onVideoEnds)
      },
    })
  }, [videoId, onVideoEnds])

  useEffect(
    () => {
      // Wistia embed code
      if (wrapperRef.current) {
        const script1 = document.createElement('script')

        script1.src = `https://fast.wistia.com/embed/medias/${videoId}.jsonp`
        script1.async = true
        const script2 = document.createElement('script')

        script2.src = 'https://fast.wistia.com/assets/external/E-v1.js'
        script2.onload = onScriptsLoad
        script2.async = true
        const div = document.createElement('div')

        div.innerHTML = `<div class="wistia_responsive_padding" style="padding:56.25% 0 0 0;position:relative;"><div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;"><div class="wistia_embed wistia_async_${videoId} autoPlay=true seo=false videoFoam=true" style="height:100%;position:relative;width:100%"><div class="wistia_swatch" style="height:100%;left:0;opacity:0;overflow:hidden;position:absolute;top:0;transition:opacity 200ms;width:100%;"><img src="https://fast.wistia.com/embed/medias/${videoId}/swatch" style="filter:blur(5px);height:100%;object-fit:contain;width:100%;" alt="" aria-hidden="true" onload="this.parentNode.style.opacity=1;" /></div></div></div></div>`
        const container = wrapperRef.current

        container.appendChild(script1)
        container.appendChild(script2)
        container.appendChild(div)
      }

      return () => {
        // Cleanup code
        if (wrapperRef.current) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          wrapperRef.current.innerHTML = ''
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videoId],
  )

  return (
    <div
      ref={wrapperRef}
      className="w-full flex-grow "
    />
  )
}
